import type { JsonSchema } from '@southfields-digital/mpxlive-components';

import type { FlowMeta, ConfigStepData, RuntimeSchemaResponse } from '../types';

import dataConnectorApiClient from './dataConnectorApiClient';

export type ProviderInfo = {
  id: string;
  name: string;
  description: string;
  basePath: string;
  endpoints: EndpointInfo[];
};

export type EndpointInfo = {
  id: string;
  name: string;
  path: string;
  configFlowId?: string;
  samplePath?: string;
};

type ProvidersResponse = {
  providers: ProviderInfo[];
};

export const getProviders = async (): Promise<ProviderInfo[]> => {
  const response = await dataConnectorApiClient.get('/providers', {});

  if (!response) {
    return [];
  }

  return (response.data as ProvidersResponse).providers;
};

export const getRuntimeData = async (
  endpoint: DataConnectorEndpoint,
  context: EndpointFlowState = {},
  signal?: AbortSignal
) => {
  const response = await dataConnectorApiClient.get(endpoint.path, context, signal);
  return response?.data;
};

export const getFlowMeta = async (params: {
  configFlowId: string;
  signal?: AbortSignal;
}): Promise<FlowMeta> => {
  const { configFlowId, signal } = params;

  const response = await dataConnectorApiClient.get(
    `/provider-config/flows/${configFlowId}`,
    {},
    signal
  );

  return response?.data as FlowMeta;
};

export const getRuntimeStep = async (params: {
  configFlowId: string;
  stepId: string;
  state: EndpointFlowState;
  signal?: AbortSignal;
}): Promise<ConfigStepData> => {
  const { configFlowId, stepId, state, signal } = params;

  const response = await dataConnectorApiClient.post(
    `/provider-config/flows/${configFlowId}/steps`,
    {
      stepId,
      state,
      runtime: true,
    },
    {},
    signal
  );

  return response?.data as ConfigStepData;
};

const mergeSchemas = (schemas: JsonSchema[]): JsonSchema => {
  const mergedProperties = schemas.reduce<JsonSchema>((acc, schema) => {
    if (schema.properties && typeof schema.properties === 'object') {
      Object.assign(acc, schema.properties);
    }
    return acc;
  }, {});

  const mergedRequired = schemas.flatMap((schema) =>
    Array.isArray(schema.required) ? schema.required : []
  );

  return {
    type: 'object',
    properties: mergedProperties,
    ...(mergedRequired.length > 0 ? { required: [...new Set(mergedRequired)] } : {}),
  };
};

export const getRuntimeSchema = async (params: {
  configFlowId: string;
  state: EndpointFlowState;
  signal?: AbortSignal;
}): Promise<RuntimeSchemaResponse> => {
  const { configFlowId, state, signal } = params;

  const flowMeta = await getFlowMeta({ configFlowId, signal });

  if (flowMeta.steps.length === 0) {
    return {
      schema: { type: 'object', properties: {} },
      steps: [],
    };
  }

  const steps: RuntimeSchemaResponse['steps'] = [];
  const schemas: JsonSchema[] = [];
  let mergedFormData: EndpointFlowState = {};

  let currentStepId: string | undefined = flowMeta.steps[0]?.id;

  while (currentStepId) {
    if (signal?.aborted) {
      throw new DOMException('Request aborted', 'AbortError');
    }

    const stepData = await getRuntimeStep({
      configFlowId,
      stepId: currentStepId,
      state,
      signal,
    });

    steps.push({
      id: stepData.id,
      label: stepData.label,
      schema: stepData.schema,
      formData: stepData.formData,
    });

    schemas.push(stepData.schema);

    if (stepData.formData) {
      mergedFormData = { ...mergedFormData, ...stepData.formData };
    }

    currentStepId = stepData.nextStepId;
  }

  return {
    schema: mergeSchemas(schemas),
    formData: Object.keys(mergedFormData).length > 0 ? mergedFormData : undefined,
    steps,
  };
};
